import { sanitizeMixpanelUrl } from './MixpanelPrivacy';

const MASKED = '[masked]';
const BLOCKED_TAGS = new Set([
  'img',
  'video',
  'canvas',
  'iframe',
  'svg',
  'object',
  'embed',
  'script',
  'noscript',
  'meta',
]);
const URL_ATTRIBUTES = new Set([
  'href',
  'src',
  'action',
  'formaction',
  'xlink:href',
  'poster',
  'background',
]);
const DIMENSION_ATTRIBUTES = new Set([
  'width',
  'height',
  'rr_width',
  'rr_height',
  'rr_scrollLeft',
  'rr_scrollTop',
  'colspan',
  'rowspan',
  'tabindex',
]);
const BOOLEAN_ATTRIBUTES = new Set([
  'disabled',
  'checked',
  'selected',
  'multiple',
  'required',
  'readonly',
  'hidden',
  'open',
]);
const STATE_VALUES = new Set([
  '',
  'true',
  'false',
  'mixed',
  'open',
  'closed',
  'checked',
  'unchecked',
  'indeterminate',
  'on',
  'off',
  'active',
  'inactive',
  'visible',
  'hidden',
  'top',
  'bottom',
  'left',
  'right',
  'start',
  'end',
]);
const INPUT_TYPES = new Set([
  'button',
  'checkbox',
  'color',
  'date',
  'datetime-local',
  'email',
  'file',
  'hidden',
  'image',
  'month',
  'number',
  'password',
  'radio',
  'range',
  'reset',
  'search',
  'submit',
  'tel',
  'text',
  'time',
  'url',
  'week',
]);
const ROLES = new Set([
  'alert',
  'button',
  'cell',
  'checkbox',
  'columnheader',
  'combobox',
  'dialog',
  'document',
  'grid',
  'group',
  'heading',
  'img',
  'link',
  'list',
  'listbox',
  'listitem',
  'main',
  'menu',
  'menuitem',
  'navigation',
  'none',
  'option',
  'presentation',
  'progressbar',
  'radio',
  'radiogroup',
  'region',
  'row',
  'rowgroup',
  'rowheader',
  'search',
  'separator',
  'slider',
  'spinbutton',
  'status',
  'switch',
  'tab',
  'table',
  'tablist',
  'tabpanel',
  'textbox',
  'tooltip',
]);
const INCREMENTAL_SOURCES = new Set([0, 1, 2, 3, 4, 5, 6, 8, 12, 13, 15]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function sanitizeAttributes(
  attributes: Record<string, unknown>,
  mirrorId: unknown,
  blocked: boolean,
): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(attributes)) {
    const name = key.toLowerCase();
    if (blocked && name !== 'class' && !DIMENSION_ATTRIBUTES.has(key)) {
      continue;
    }
    if (value === null) {
      sanitized[key] = null;
    } else if (name === 'class' && typeof value === 'string') {
      sanitized[key] = value;
    } else if (name === 'value' && typeof value === 'string') {
      // The SDK applies sensitive-field masking before this filter runs.
      sanitized[key] = value;
    } else if (name === 'style' || name === '_csstext') {
      sanitized[key] = value;
    } else if (name === 'id') {
      sanitized[key] =
        value === 'appRoot'
          ? value
          : typeof mirrorId === 'number'
          ? `replay-node-${mirrorId}`
          : MASKED;
    } else if (URL_ATTRIBUTES.has(name)) {
      sanitized[key] = sanitizeMixpanelUrl(value);
    } else if (name === 'srcset') {
      sanitized[key] = '';
    } else if (BOOLEAN_ATTRIBUTES.has(name)) {
      sanitized[key] = typeof value === 'boolean' ? value : '';
    } else if (
      DIMENSION_ATTRIBUTES.has(key) &&
      (typeof value === 'number' ||
        (typeof value === 'string' && /^-?[\d.]+(?:px|%)?$/.test(value)))
    ) {
      sanitized[key] = value;
    } else if (name === 'type' && typeof value === 'string' && INPUT_TYPES.has(value)) {
      sanitized[key] = value;
    } else if (name === 'role' && typeof value === 'string' && ROLES.has(value)) {
      sanitized[key] = value;
    } else if (name === 'dir' && (value === 'ltr' || value === 'rtl' || value === 'auto')) {
      sanitized[key] = value;
    } else if (
      name === 'lang' &&
      typeof value === 'string' &&
      /^[a-z]{2,3}(?:-[a-z0-9]{2,8})*$/i.test(value)
    ) {
      sanitized[key] = value;
    } else if (
      (name.startsWith('aria-') || name.startsWith('data-')) &&
      typeof value === 'string' &&
      STATE_VALUES.has(value)
    ) {
      sanitized[key] = value;
    } else {
      sanitized[key] = MASKED;
    }
  }
  return sanitized;
}

function sanitizeValue(value: unknown, key = ''): unknown {
  if (typeof value === 'string') {
    if (key === 'href' || key === 'url' || key === 'systemId') {
      return value ? sanitizeMixpanelUrl(value) : '';
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((entry: unknown) => sanitizeValue(entry, key));
  }
  if (!isRecord(value)) {
    return value;
  }
  const attributes = isRecord(value.attributes) ? value.attributes : null;
  const blocked =
    (typeof value.tagName === 'string' && BLOCKED_TAGS.has(value.tagName.toLowerCase())) ||
    (attributes !== null && 'data-replay-block' in attributes);
  const sanitized: Record<string, unknown> = {};
  for (const [property, entry] of Object.entries(value)) {
    if (property === 'attributes' && isRecord(entry)) {
      sanitized[property] = sanitizeAttributes(entry, value.id, blocked);
    } else if (property === 'childNodes' && blocked) {
      sanitized[property] = [];
    } else {
      sanitized[property] = sanitizeValue(entry, property);
    }
  }
  return sanitized;
}

export function sanitizeMixpanelReplayEvent(event: unknown): unknown | null {
  if (
    !isRecord(event) ||
    typeof event.type !== 'number' ||
    ![0, 1, 2, 3, 4].includes(event.type) ||
    typeof event.timestamp !== 'number' ||
    !Number.isFinite(event.timestamp) ||
    !isRecord(event.data)
  ) {
    return null;
  }
  if (
    event.type === 3 &&
    (typeof event.data.source !== 'number' || !INCREMENTAL_SOURCES.has(event.data.source))
  ) {
    return null;
  }
  try {
    return sanitizeValue(event);
  } catch {
    return null;
  }
}
